import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';

/**
 * AuthHeaderInterceptor automatically injects:
 * - X-User-Roles: List of roles of the authenticated user
 * - X-User-UPN: User Principal Name (email/username)
 *
 * This ensures audit trails and role-based access filtering are transparently
 * handled for all backend requests.
 */
@Injectable()
export class AuthHeaderInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const user = this.authService.getCurrentUser();
    let headers = request.headers;

    // Extract cookie token using the environment-configured name (varies by environment).
    // Falls back to the MSAL access token cached at login (required by desarrolla-agent on 8001).
    const cookieName = environment.ssoCookieName;
    const cookieToken = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${cookieName}=`))
      ?.split('=')[1];
    const bearerToken = cookieToken || this.authService.getCachedMsalToken();

    if (bearerToken && !request.headers.has('Authorization')) {
      headers = headers.set('Authorization', `Bearer ${bearerToken}`);
    }

    if (user) {
      if (user.roles?.length && !request.headers.has('X-User-Roles')) {
        headers = headers.set('X-User-Roles', user.roles.join(','));
      }
      if (user.userPrincipalName && !request.headers.has('X-User-UPN')) {
        headers = headers.set('X-User-UPN', user.userPrincipalName);
      }
      if (user.displayName && !request.headers.has('X-User-Name')) {
        headers = headers.set('X-User-Name', user.displayName);
      }
      if (user.id && !request.headers.has('X-User-Id')) {
        headers = headers.set('X-User-Id', user.id);
      }
    }
    
    const clonedRequest = request.clone({ headers });

    return next.handle(clonedRequest);
  }
}
